/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, Flex, Table } from "@powerledger/ui-component-lib";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";

import { calculateTotalTradedVolume } from "@/app/components/csv-components/trading-summary";
import { currencyFormatter } from "@/app/lib/currency-formatter";
import { getTableTranslation } from "@/app/lib/get-translations-for-components";
import { volumeFormatter } from "@/app/lib/volume-formatter";

import { TableDataType, TradingSummaryTableProps } from "./trading-summary-table.types";

const getCurrencyColumn = (text: string, currencySymbol: string, accessor: keyof TableDataType) => ({
  Header: <Box sx={{ maxWidth: "200px", ml: "auto", textAlign: "right" }}>{text}</Box>,
  accessor: (row: TableDataType) => row[accessor].amount,
  id: accessor,
  Cell: ({ value }: { value: string }) => (
    <Box
      sx={{
        textAlign: "right",
        maxWidth: "200px",
        color: "textDarker",
        px: 2,
      }}
    >
      {currencyFormatter(currencySymbol, value)}
    </Box>
  ),
});

export const TradingSummaryTable: React.FC<TradingSummaryTableProps> = ({
  currencySymbol,
  tradingSummary,
  loading,
}) => {
  const { t } = useTranslation();
  const tableColumns: Column<TableDataType>[] = useMemo(
    () => [
      {
        Header: t("Membership ID"),
        accessor: (row) => row.account.members?.[0].membershipId ?? "-",
        Cell: ({ value }: { value: string }) => <Flex sx={{ color: "textDarker" }}>{value || "-"}</Flex>,
      },
      {
        Header: t("Member Name"),
        accessor: (row) => row.account.members?.[0].name ?? "-",
        Cell: ({ value }: { value: string }) => <Flex sx={{ color: "textDarker" }}>{value || "-"}</Flex>,
      },
      {
        Header: t("Authorised User Email"),
        accessor: (row) => row.account.email,
        id: "email",
        Cell: ({ value }: { value: string }) => <Flex sx={{ color: "textDarker" }}>{value || "-"}</Flex>,
      },
      {
        Header: t("Carbon Credits Traded"),
        accessor: (row) => calculateTotalTradedVolume(row),
        Cell: ({ value }: { value: number }) => <Flex sx={{ color: "textDarker" }}>{volumeFormatter(value)}</Flex>,
      },
      {
        Header: t("Sold"),
        accessor: (row) => row.tradedAskVolume,
        Cell: ({ value }: { value: number }) => <Flex sx={{ color: "textDarker" }}>{volumeFormatter(value ?? 0)}</Flex>,
      },
      getCurrencyColumn(t("Trade GST Received"), currencySymbol, "totalTradeGSTReceived"),
      getCurrencyColumn(t("Received Amount"), currencySymbol, "receivedAmount"),
      getCurrencyColumn(t("Seller Fees"), currencySymbol, "totalSellerFees"),
      getCurrencyColumn(t("Seller Fees GST"), currencySymbol, "totalSellerFeesGST"),
      {
        Header: t("Bought"),
        accessor: (row) => row.tradedBidVolume,
        Cell: ({ value }: { value: number }) => <Flex sx={{ color: "textDarker" }}>{volumeFormatter(value ?? 0)}</Flex>,
      },
      getCurrencyColumn(t("Trade GST Paid"), currencySymbol, "totalTradeGSTPaid"),
      getCurrencyColumn(t("Paid Amount"), currencySymbol, "paidAmount"),
      getCurrencyColumn(t("Buyer Fees"), currencySymbol, "totalBuyerFees"),
      getCurrencyColumn(t("Buyer Fees GST"), currencySymbol, "totalBuyerFeesGST"),
    ],
    [t, currencySymbol],
  );

  return (
    <Table
      columns={tableColumns}
      dataSource={tradingSummary?.tradeReportDetails ?? []}
      pagination={false}
      loading={loading}
      showScrollArrows
      translation={getTableTranslation(t)}
    />
  );
};
